import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import ThemeToggle from "@/components/ThemeToggle";
import { LOGO_URL } from "@/lib/api";
import { House, Users, Globe, CreditCard, Ticket, GearSix, Megaphone, ChartLine, Shield, SignOut, MapTrifold, TreeStructure, Pulse, SealCheck, Rocket, Storefront, Flag, CalendarBlank, Confetti, Gift, Trophy } from "@phosphor-icons/react";

// `badge` maps this nav item to a key inside GET /api/admin/nav-counts.
// A red dot with the count is shown whenever that count is > 0.
const items = [
  { to: "/admin", label: "Dashboard", icon: ChartLine, end: true, testid: "admin-nav-dashboard" },
  { to: "/admin/activity", label: "Activity", icon: Pulse, testid: "admin-nav-activity" },
  { to: "/admin/visitors", label: "Visitors", icon: MapTrifold, testid: "admin-nav-visitors" },
  { to: "/admin/users", label: "Users", icon: Users, testid: "admin-nav-users" },
  { to: "/admin/websites", label: "Websites", icon: Globe, testid: "admin-nav-websites", badge: "websites" },
  { to: "/admin/reports", label: "Reports", icon: Flag, testid: "admin-nav-reports", badge: "reports" },
  { to: "/admin/splash", label: "Splash pages", icon: Rocket, testid: "admin-nav-splash" },
  { to: "/admin/start-pages", label: "Start pages", icon: CalendarBlank, testid: "admin-nav-start-pages", badge: "start_pages" },
  { to: "/admin/payments", label: "Payments", icon: CreditCard, testid: "admin-nav-payments", badge: "payments" },
  { to: "/admin/store", label: "Store links", icon: Storefront, testid: "admin-nav-store" },
  { to: "/admin/plans", label: "Plans", icon: Shield, testid: "admin-nav-plans" },
  { to: "/admin/referrals", label: "Referrals", icon: TreeStructure, testid: "admin-nav-referrals" },
  { to: "/admin/verification", label: "Verification", icon: SealCheck, testid: "admin-nav-verification", badge: "verification" },
  { to: "/admin/tickets", label: "Tickets", icon: Ticket, testid: "admin-nav-tickets", badge: "support" },
  { to: "/admin/announcements", label: "Announcements", icon: Megaphone, testid: "admin-nav-announcements" },
  { to: "/admin/login-deals", label: "Login deals", icon: Confetti, testid: "admin-nav-login-deals" },
  { to: "/admin/surf-deals", label: "Surf deals", icon: Confetti, testid: "admin-nav-surf-deals" },
  { to: "/admin/prize-pages", label: "Prize pages", icon: Gift, testid: "admin-nav-prize-pages" },
  { to: "/admin/draws", label: "Surf Drawn", icon: Trophy, testid: "admin-nav-draws" },
  { to: "/admin/settings", label: "Settings", icon: GearSix, testid: "admin-nav-settings" },
];

export default function AdminLayout({ children }) {
  const { user, logout, siteSettings } = useAuth();
  const nav = useNavigate();
  const [counts, setCounts] = useState({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/nav-counts");
        if (!cancelled) setCounts(data || {});
      } catch { /* silently ignore polling failure */ }
    };
    load();
    const t = setInterval(load, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const totalOpen = Object.values(counts).reduce((sum, n) => sum + (Number(n) || 0), 0);

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950 text-slate-900 dark:text-slate-100" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <header className="sticky top-0 z-40 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border-b border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="max-w-[1500px] mx-auto px-4 h-24 flex items-center gap-4">
          <div className="flex items-center gap-3">
            <img src={siteSettings?.logo_url || LOGO_URL} alt="logo" className="h-20" />
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-yellow-600 dark:text-yellow-400">
                Admin Console {totalOpen > 0 && (
                  <span data-testid="admin-total-open-badge" className="ml-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-black px-2 py-[1px]">
                    {totalOpen}
                  </span>
                )}
              </div>
              <div className="text-sm font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{siteSettings?.site_name || "Start-A-TE"}</div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            <button data-testid="admin-back-btn" onClick={() => nav("/dashboard")} className="rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-sm font-bold px-4 py-2 inline-flex items-center gap-2 transition-colors duration-200"><House size={16} weight="fill" /> Site</button>
            <div className="hidden sm:block text-sm text-right">
              <div className="font-semibold text-slate-900 dark:text-white">{user?.username}</div>
              <div className="text-[10px] uppercase tracking-widest text-yellow-600 dark:text-yellow-400">Administrator</div>
            </div>
            <button data-testid="admin-logout-btn" onClick={async () => { await logout(); nav("/"); }} className="h-9 w-9 rounded-full bg-red-500 hover:bg-red-600 text-white inline-flex items-center justify-center transition-colors duration-200"><SignOut size={16} weight="bold" /></button>
          </div>
        </div>
      </header>
      <div className="max-w-[1500px] mx-auto px-4 py-6 grid lg:grid-cols-[220px_1fr] gap-6">
        <aside className="lg:sticky lg:top-24 h-fit space-y-1 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-2">
          {items.map((i) => {
            const count = i.badge ? Number(counts[i.badge] || 0) : 0;
            return (
              <NavLink
                key={i.to}
                to={i.to}
                end={i.end}
                data-testid={i.testid}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold transition-colors duration-200 ${
                    isActive ? "bg-yellow-400 text-yellow-950" : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`
                }
              >
                <i.icon size={18} weight="bold" />
                <span className="flex-1">{i.label}</span>
                {count > 0 && (
                  <span
                    data-testid={`${i.testid}-badge`}
                    aria-label={`${count} pending`}
                    className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-black leading-none shadow-[0_0_0_2px_rgba(239,68,68,0.25)]"
                  >
                    {count > 99 ? "99+" : count}
                  </span>
                )}
              </NavLink>
            );
          })}
        </aside>
        <main>{children || <Outlet />}</main>
      </div>
    </div>
  );
}
